import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicCartAPI, CustomerOrdersAPI, getProductImageUrl } from '../../Services/ECommerceAPI';
import { notifyCartUpdated } from '../../Layout/PublicLayout';
import { toast } from 'react-toastify';
import './ShoppingCart.css';

const ShoppingCart = () => {
    const [cart, setCart] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        checkAuth();
        loadCart();
    }, []);

    const checkAuth = () => {
        const token = sessionStorage.getItem('token');
        setIsAuthenticated(!!token);
    };

    const loadCart = async () => {
        try {
            setLoading(true);
            let cartData;
            const token = sessionStorage.getItem('token');

            if (token) {
                cartData = await CustomerOrdersAPI.getCart();
            } else {
                const sessionId = sessionStorage.getItem('cartSessionId');
                if (!sessionId) {
                    setCartItems([]);
                    setLoading(false);
                    return;
                }
                cartData = await PublicCartAPI.getCart(sessionId);
            }

            setCart(cartData);
            setCartItems(cartData?.cartItems || []);
        } catch (error) {
            console.error('Error loading cart:', error);
            setCartItems([]);
        } finally {
            setLoading(false);
        }
    };

    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity <= 0) {
            await removeItem(itemId);
            return;
        }

        try {
            if (isAuthenticated) {
                await CustomerOrdersAPI.updateCartItem(itemId, newQuantity);
            } else {
                await PublicCartAPI.updateItem(itemId, newQuantity);
            }
            await loadCart();
            notifyCartUpdated();
            toast.success('Cart updated');
        } catch (error) {
            console.error('Error updating cart:', error);
            toast.error('Failed to update cart item');
        }
    };

    const removeItem = async (itemId) => {
        try {
            if (isAuthenticated) {
                await CustomerOrdersAPI.removeCartItem(itemId);
            } else {
                await PublicCartAPI.removeItem(itemId);
            }
            await loadCart();
            notifyCartUpdated();
            toast.success('Item removed from cart');
        } catch (error) {
            console.error('Error removing item:', error);
            toast.error('Failed to remove item');
        }
    };

    const calculateSubtotal = () => {
        return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const tax = subtotal * 0.1; // 10% tax (adjust as needed)
        const shipping = subtotal > 100 ? 0 : 10; // Free shipping over $100
        return subtotal + tax + shipping;
    };

    if (loading) {
        return <div className="cart-loading">Loading cart...</div>;
    }

    if (cartItems.length === 0) {
        return (
            <div className="cart-container">
                <div className="empty-cart">
                    <h2>Your cart is empty</h2>
                    <p>Start shopping to add items to your cart</p>
                    <button onClick={() => navigate('/shop')} className="shop-button">
                        Continue Shopping
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-container">
            <h1>Shopping Cart</h1>
            
            <div className="cart-content">
                <div className="cart-items">
                    {cartItems.map(item => (
                        <div key={item.cartItemID} className="cart-item">
                            <div className="item-image">
                                {item.product?.images?.[0] ? (
                                    <img
                                        src={getProductImageUrl(item.product.images[0].imageUrl)}
                                        alt={item.product.name}
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                                        }}
                                    />
                                ) : (
                                    <div className="no-image">No Image</div>
                                )}
                            </div>
                            
                            <div className="item-details">
                                <h3 onClick={() => navigate(`/product/${item.product?.slug || item.productID}`)}>
                                    {item.product?.name || 'Product'}
                                </h3>
                                <p className="item-price">${item.price.toFixed(2)}</p>
                            </div>

                            <div className="item-quantity">
                                <button
                                    onClick={() => updateQuantity(item.cartItemID, item.quantity - 1)}
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => {
                                        const newQty = parseInt(e.target.value) || 1;
                                        updateQuantity(item.cartItemID, newQty);
                                    }}
                                    min="1"
                                />
                                <button
                                    onClick={() => updateQuantity(item.cartItemID, item.quantity + 1)}
                                >
                                    +
                                </button>
                            </div>

                            <div className="item-total">
                                <span>${(item.price * item.quantity).toFixed(2)}</span>
                            </div>

                            <button
                                className="remove-item"
                                onClick={() => removeItem(item.cartItemID)}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>

                <div className="cart-summary">
                    <h2>Order Summary</h2>
                    <div className="summary-row">
                        <span>Subtotal:</span>
                        <span>${calculateSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                        <span>Tax:</span>
                        <span>${(calculateSubtotal() * 0.1).toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                        <span>Shipping:</span>
                        <span>{calculateSubtotal() > 100 ? 'Free' : '$10.00'}</span>
                    </div>
                    <div className="summary-row total">
                        <span>Total:</span>
                        <span>${calculateTotal().toFixed(2)}</span>
                    </div>
                    <button
                        className="checkout-button"
                        onClick={() => {
                            const sid = sessionStorage.getItem('cartSessionId');
                            if (sid) {
                                navigate(`/checkout?sid=${encodeURIComponent(sid)}`);
                            } else {
                                navigate('/checkout');
                            }
                        }}
                    >
                        Proceed to Checkout
                    </button>
                    <button
                        className="continue-shopping-button"
                        onClick={() => navigate('/shop')}
                    >
                        Continue Shopping
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShoppingCart;
